import axios from 'axios';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Save, Copy, MessageSquare, Calendar, Home, TreePine, Layers, Brain } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendrierRdvEmbed } from '@/components/crm/CalendrierRdvEmbed';
import { useEmailPreview, EmailPreviewFrame } from '@/components/EmailPreview';
import { RdvEmailConfirmationDialog } from '@/components/crm/RdvEmailConfirmationDialog';
import { GooglePlacesAutocomplete } from '@/components/shared/GooglePlacesAutocomplete';  // 🐛 [Phase 99 Bonus]

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// ─── Questions par défaut ──────────────────────────────────────────────────
const QUESTIONS_DEFAUT_INTERIEUR = [
  "Les murs sont-ils en bon état ? (fissures, trous, dommages)",
  "Y a-t-il des plafonds à peindre ?",
  "Des portes intérieures à peindre ?",
  "Des boiseries / moulures à inclure ?",
  "Y a-t-il des armoires à peindre ?",
  "Escalier intérieur à inclure ?",
  "Sous-sol à inclure dans les travaux ?",
  "Garage à inclure ?",
  "Y a-t-il des réparations de surfaces requises ?",
  "Le client sera-t-il présent lors des travaux ?",
  "Travaux à réaliser en plusieurs phases ?",
  "Contraintes d'accès particulières ?",
];

const QUESTIONS_DEFAUT_EXTERIEUR = [
  "La façade est-elle en bon état général ?",
  "Y a-t-il du bois pourri ou à remplacer ?",
  "Galerie / balcon à inclure ?",
  "Soffite / fascia à peindre ?",
  "Escaliers extérieurs à inclure ?",
  "Garde-corps extérieur à peindre ?",
  "Cabanon / remise inclus ?",
  "Clôture à inclure ?",
  "Accès à l'électricité disponible sur place ?",
  "Permis municipal requis ?",
  "Peinture sur brique ou béton ?",
  "Travaux de calfeutrage requis ?",
];

function buildQuestionsDefaut(labels) {
  return labels.map((q, i) => ({ id: `q_${i}_${Date.now()}`, question: q, reponse: null, commentaire: '' }));
}

// ─── Ligne de question rapide ─────────────────────────────────────────────
function QuestionRapide({ item, onReponse, index }) {
  const rowClass = item.reponse === 'oui' ? 'border-green-200 bg-green-50'
    : item.reponse === 'non' ? 'border-red-200 bg-red-50'
    : item.reponse === 'na' ? 'border-gray-100 bg-gray-50'
    : 'border-gray-200 bg-white';
  return (
    <div className={`border rounded-lg px-3 py-2.5 flex items-center gap-3 transition-all ${rowClass}`}>
      <span className="w-5 text-xs text-gray-400 text-right flex-shrink-0">{index + 1}.</span>
      <span className="flex-1 text-sm text-gray-700 leading-snug">{item.question}</span>
      <div className="flex gap-1 flex-shrink-0">
        {[
          { key: 'oui', label: 'Oui', on: 'bg-green-500 text-white border-green-500', off: 'text-gray-500 border-gray-200 hover:border-green-400 hover:text-green-600' },
          { key: 'non', label: 'Non', on: 'bg-red-500 text-white border-red-500', off: 'text-gray-500 border-gray-200 hover:border-red-400 hover:text-red-600' },
          { key: 'na',  label: 'N/A', on: 'bg-gray-400 text-white border-gray-400', off: 'text-gray-400 border-gray-200 hover:border-gray-400 hover:text-gray-600' },
        ].map(({ key, label, on, off }) => (
          <button
            key={key}
            type="button"
            data-testid={`creer-q-${key}-${item.id}`}
            onClick={() => onReponse(item.reponse === key ? null : key)}
            className={`px-2.5 py-0.5 text-xs font-semibold rounded border transition-all bg-white ${item.reponse === key ? on : off}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Suggestion IA de plage horaire ─────────────────────────────────────────
function SuggestionRdv({ adresse, ville, representantNom }) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState("");

  const getSuggestion = async () => {
    if (!adresse || !ville) {
      setError("Remplissez d'abord l'adresse des travaux (section 3) pour obtenir une suggestion.");
      return;
    }
    setLoading(true);
    setError("");
    setSuggestion(null);
    try {
      // Récupérer les RDV existants des 7 prochains jours
      const rdvRes = await axios.get(`${API_URL}/api/crm/rendez-vous?limite=20`);
      const rdvData = rdvRes.ok ? rdvRes.data : {};
      const rdvsExistants = (rdvData.rendez_vous || []).slice(0, 10).map(r => ({
        date: r.date_rdv,
        heure: r.heure_rdv,
        adresse: r.adresse_client || "",
        ville: r.ville_client || "",
        representant: r.representant_nom || "",
      }));

      const r = await axios.post(`${API_URL}/api/chat-ia/rdv-suggestion`, {
          adresse,
          ville,
          representant_nom: representantNom || "",
          rdvs_existants: rdvsExistants,
        });
      if (r.status === 200) {
        const d = r.data;
        setSuggestion(d);
      } else {
        setError("Impossible d'obtenir une suggestion pour l'instant.");
      }
    } catch { setError("Erreur de connexion à l'IA."); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-indigo-600" />
          <p className="text-sm font-semibold text-indigo-700">Suggestion IA — Meilleure plage horaire</p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={getSuggestion}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1.5 h-7"
          data-testid="btn-suggestion-rdv"
        >
          {loading ? "Analyse..." : "Obtenir suggestion"}
        </Button>
      </div>
      <p className="text-xs text-indigo-500 mb-2">
        Basé sur l'adresse des travaux et les autres RDV planifiés.
      </p>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {suggestion && (
        <div className="bg-white border border-indigo-200 rounded-lg p-3 space-y-1">
          <p className="text-sm font-bold text-indigo-800">
            Suggestion : {suggestion.suggestion}
          </p>
          {suggestion.raison && <p className="text-xs text-indigo-600">{suggestion.raison}</p>}
          {suggestion.rdv_proche && (
            <p className="text-xs text-gray-500">À regrouper avec : {suggestion.rdv_proche}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function CreerDossier() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false); // Prévient les doubles soumissions
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);

  // États questionnaire notes
  const [questionsInt, setQuestionsInt] = useState(() => buildQuestionsDefaut(QUESTIONS_DEFAUT_INTERIEUR));
  const [questionsExt, setQuestionsExt] = useState(() => buildQuestionsDefaut(QUESTIONS_DEFAUT_EXTERIEUR));

  const [formData, setFormData] = useState({
    // Infos Client
    client_nom: '',
    client_type: 'residentiel',
    client_telephone: '',
    client_email: '',
    client_adresse: '',
    client_ville: '',
    client_code_postal: '',
    // 🐛 [v13.63 Phase 2] Coords autocomplete client (persistées si fournies)
    client_latitude: null,
    client_longitude: null,
    
    // Infos Dossier
    nom_dossier: '',
    type_contrat: 'peinture',
    departement: 'PRQC',
    date_debut_prevue: '',
    date_creation: new Date().toISOString().split('T')[0],
    type_travaux: '',
    categorie_travaux: '',
    
    // Adresse Travaux
    adresse_travaux: '',
    ville_travaux: '',
    code_postal_travaux: '',
    // 🐛 [v13.63 Phase 2] Coords autocomplete travaux (persistées si fournies)
    latitude_travaux: null,
    longitude_travaux: null,
    utilise_adresse_client_travaux: false,
    
    // Assignation (REPRÉSENTANT seulement)
    representant_id: '',
    representant_nom: '',
    
    // Notes pour la soumission (temporaire, sauvegardées dans les notes du dossier)
    notes_projet: '',
  });

  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [emailData, setEmailData] = useState({ sujet: '', contenu: '', _preview: false });
  const { previewHtml: dossierPreviewHtml, loadingPreview: dossierLoadingPreview, fetchPreview: dossierFetchPreview } = useEmailPreview();
  const [createdDossierId, setCreatedDossierId] = useState(null);
  const [createdRdvId, setCreatedRdvId] = useState(null);
  const [rdvScheduled, setRdvScheduled] = useState(false);
  const [templates, setTemplates] = useState([]);
  // 🆕 2026-04-30 — Template RDV + infos entreprise (pour générer un contenu identique au Calendrier RDV)
  const [rdvTemplate, setRdvTemplate] = useState(null);
  const [infosEntreprise, setInfosEntreprise] = useState({});
  const [sendingRdvEmail, setSendingRdvEmail] = useState(false);
  const [categoriesSuggestions, setCategoriesSuggestions] = useState([]);
  // RDV sélectionné via le calendrier embarqué
  const [selectedRdvSlot, setSelectedRdvSlot] = useState(null); // { date, heure, employe_id, employe_nom }
  // Option "Soumission à faire" sans RDV
  const [soumAFaireSansRdv, setSoumAFaireSansRdv] = useState(false);
  const [dateEcheanceSoum, setDateEcheanceSoum] = useState('');

  // Charger les employés et templates au montage du composant
  useEffect(() => {
    fetchEmployees();
    axios.get(`${API_URL}/api/templates`).then(r => r.data)
      .then(d => setTemplates(d.templates || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/crm/categories-travaux`).then(r => r.data)
      .then(d => setCategoriesSuggestions(d.categories || []))
      .catch(() => {});
    // 🆕 2026-04-30 — Pré-charger le template RDV + infos entreprise pour aligner
    // le contenu courriel avec celui du Calendrier de prise de RDV.
    axios.get(`${API_URL}/api/messages-templates?categorie=rendez_vous`).then(r => r.data)
      .then(d => setRdvTemplate((d.templates || [])[0] || null))
      .catch(() => {});
    axios.get(`${API_URL}/api/entreprise/infos`).then(r => r.data)
      .then(d => setInfosEntreprise(d || {}))
      .catch(() => {});
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employees?include_owners=true&include_representants_users=true`);
      if (response.status !== 200) throw new Error('Erreur');
      const data = response.data;
      // 🆕 [3B] Filtre élargi : utiliser is_representant + plusieurs champs role/type_employe/role_admin
      // pour inclure les proprietaires-représentants (ex: Nass).
      const VALID = new Set(['proprietaire', 'charge_projet', 'adjoint_admin', 'adjoint', 'representant', 'representante', 'rep']);
      setEmployees((data.employees || []).filter(e => {
        if (e.is_representant === true) return true;
        const candidates = [e.role, e.type_employe, e.role_admin, e.role_terrain]
          .filter(Boolean)
          .map(v => String(v).toLowerCase());
        return candidates.some(v => VALID.has(v));
      }));
    } catch (error) {
      console.error('Erreur:', error);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleRepresentantChange = (employeeId) => {
    if (employeeId === 'aucun') {
      setFormData((prev) => ({
        ...prev,
        representant_id: '',
        representant_nom: '',
      }));
    } else {
      const employee = employees.find((e) => e.id === employeeId);
      if (employee) {
        setFormData((prev) => ({
          ...prev,
          representant_id: employeeId,
          representant_nom: `${employee.prenom} ${employee.nom}`,
        }));
      }
    }
  };

  const handleUtiliserAdresseClient = (checked) => {
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        utilise_adresse_client_travaux: true,
        adresse_travaux: prev.client_adresse,
        ville_travaux: prev.client_ville,
        code_postal_travaux: prev.client_code_postal,
        // 🐛 [v13.63 Phase 2] Propager aussi les coords client → travaux
        //   quand l'utilisatrice coche « Utiliser l'adresse du client »
        latitude_travaux: prev.client_latitude ?? null,
        longitude_travaux: prev.client_longitude ?? null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        utilise_adresse_client_travaux: false,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Prévenir les doubles soumissions (ref synchrone)
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    // Validation — reset le ref si validation échoue
    if (!formData.client_nom || !formData.client_telephone || !formData.client_adresse) {
      toast.error('Veuillez remplir toutes les informations du client');
      isSubmittingRef.current = false;
      return;
    }

    if (!formData.nom_dossier || !formData.adresse_travaux) {
      toast.error('Veuillez remplir toutes les informations du dossier');
      isSubmittingRef.current = false;
      return;
    }

    if (!formData.date_creation) {
      toast.error('Veuillez indiquer la date de création du projet');
      isSubmittingRef.current = false;
      return;
    }

    setLoading(true);

    try {
      // Étape 1 : Créer le client
      const clientPayload = {
        nom: formData.client_nom,
        type_client: formData.client_type,
        telephone: formData.client_telephone,
        email: formData.client_email || null,
        adresse_client: formData.client_adresse,
        ville_client: formData.client_ville,
        code_postal_client: formData.client_code_postal,
        utilise_adresse_client_facturation: true,
        actif: true,
        // 🐛 [v13.63 Phase 2] Fix B1 : persister les coords de l'adresse
        //   client (extra fields grâce à model_config={extra:allow}).
        //   Utile pour phase 3 (chantiers près de moi) + backfill futur.
        latitude_client: formData.client_latitude ?? null,
        longitude_client: formData.client_longitude ?? null,
      };

      const clientResponse = await axios.post(`${API_URL}/api/crm/clients`, clientPayload);

      if (clientResponse.status !== 200) throw new Error('Erreur lors de la création du client');

      const clientData = clientResponse.data;
      const clientId = clientData.client_id;

      // Étape 2 : Créer le dossier
      const dateCreation = formData.date_creation
        ? new Date(formData.date_creation + 'T12:00:00').toISOString()
        : new Date().toISOString();
      const dossierPayload = {
        client_id: clientId,
        client_nom: formData.client_nom,
        nom_dossier: formData.nom_dossier,
        type_contrat: formData.type_contrat,
        departement: formData.departement,
        adresse_travaux: formData.adresse_travaux,
        ville_travaux: formData.ville_travaux,
        code_postal_travaux: formData.code_postal_travaux,
        utilise_adresse_client_travaux: formData.utilise_adresse_client_travaux,
        // 🐛 [v13.63 Phase 2] Fix B1 : persister les coords retournées
        //   par l'autocomplete Google Places à la CRÉATION. Auparavant
        //   `Dossier.latitude_travaux` restait null → banner « Adresse
        //   client non géolocalisée » + géocodage lazy backend
        //   dépendant de Geocoding API. Modèle Dossier accepte déjà
        //   `latitude_travaux: Optional[float]` (models_crm.py L66-67).
        latitude_travaux: formData.latitude_travaux ?? null,
        longitude_travaux: formData.longitude_travaux ?? null,
        representant_id: formData.representant_id || null,
        representant_nom: formData.representant_nom || null,
        date_debut_prevue: formData.date_debut_prevue || null,
        date_creation: dateCreation,
        type_travaux: formData.type_travaux || null,
        categorie_travaux: formData.categorie_travaux || null,
      };

      const dossierResponse = await axios.post(`${API_URL}/api/crm/dossiers`, dossierPayload);

      if (dossierResponse.status !== 200) throw new Error('Erreur lors de la création du dossier');

      const dossierData = dossierResponse.data;
      
      // Étape 3 : Sauvegarder le questionnaire + notes dans le dossier
      const hasQuestionnaire = !!formData.type_travaux;
      const hasNotes = formData.notes_projet.trim();
      if (hasQuestionnaire || hasNotes) {
        try {
          await axios.patch(`${API_URL}/api/crm/dossiers/${dossierData.dossier_id}/checklists`, {
            questionnaire_type: formData.type_travaux || null,
            questions_interieur: questionsInt,
            questions_exterieur: questionsExt,
            notes_inspection: formData.notes_projet.trim()
          });
        } catch (err) {
          console.error('Erreur sauvegarde questionnaire:', err);
        }
      }

      // Étape 4: Créer le RDV si un créneau a été sélectionné dans le calendrier
      if (selectedRdvSlot && selectedRdvSlot.date && selectedRdvSlot.heure) {
        const repId = selectedRdvSlot.employe_id || formData.representant_id;
        const repNom = selectedRdvSlot.employe_nom || formData.representant_nom;
        const [h, m] = selectedRdvSlot.heure.split(':');
        const heureFin = `${(parseInt(h) + 1).toString().padStart(2, '0')}:${m}`;

        const rdvPayload = {
          type_rdv: 'soumission',
          titre: `Soumission - ${formData.client_nom}`,
          dossier_id: dossierData.dossier_id,
          dossier_nom: formData.nom_dossier,
          client_id: clientId,
          client_nom: formData.client_nom,
          client_email: formData.client_email || '',
          client_telephone: formData.client_telephone,
          adresse_travaux: formData.adresse_travaux,
          ville_travaux: formData.ville_travaux,
          code_postal_travaux: formData.code_postal_travaux,
          employe_id: repId || '',
          employe_nom: repNom || '',
          date: selectedRdvSlot.date,
          heure_debut: selectedRdvSlot.heure,
          heure_fin: heureFin,
          notes: formData.notes_projet || '',
          // ✅ FIX 2026-04-22 : demander au backend de NE PAS envoyer l'email automatiquement
          // pour que l'utilisateur puisse voir un aperçu avant.
          skip_auto_email: Boolean(formData.client_email),
        };

        const rdvRes = await axios.post(`${API_URL}/api/crm/rendez-vous`, rdvPayload);

        if (rdvRes.status === 200) {
          setRdvScheduled(true);
          const createdRdvId = rdvRes.data?.rdv_id;
          // Si un email client est présent → afficher l'aperçu avant envoi
          if (formData.client_email && createdRdvId) {
            try {
              // 🆕 2026-04-30 — Générer un contenu IDENTIQUE à celui de CalendrierRendezVous.
              // (Avant : on affichait /preview-email → HTML uniquement, contenu non éditable.)
              const rep = (employees || []).find(e => e.id === repId);
              const heureFinDisplay = heureFin ? `${selectedRdvSlot.heure} et ${heureFin}` : selectedRdvSlot.heure;
              const vars = {
                client_nom: `${formData.client_prenom || ''} ${formData.client_nom || ''}`.trim(),
                date_rdv: selectedRdvSlot.date,
                heure_rdv: heureFin ? `entre ${heureFinDisplay}` : selectedRdvSlot.heure,
                representant: repNom || '',
                telephone_representant: rep?.telephone || '',
                adresse_travaux: formData.adresse_travaux || '',
                lien_confirmation: '(Boutons de confirmation ajoutés automatiquement)',
                nom_entreprise: infosEntreprise?.nom_entreprise || 'Peinture Résidentielle Qc Inc.',
                telephone_entreprise: infosEntreprise?.telephone || '',
                adresse_entreprise: infosEntreprise?.adresse || '',
                numero_rbq: infosEntreprise?.numero_rbq ? `RBQ : ${infosEntreprise.numero_rbq}` : '',
                logo_email: '',
              };
              let sujet, contenu;
              if (rdvTemplate) {
                sujet = rdvTemplate.sujet || 'Confirmation de votre rendez-vous';
                contenu = rdvTemplate.contenu || '';
                Object.entries(vars).forEach(([k, v]) => {
                  sujet = sujet.replace(new RegExp(`\\{${k}\\}`, 'g'), v || '');
                  contenu = contenu.replace(new RegExp(`\\{${k}\\}`, 'g'), v || '');
                });
                contenu = contenu.replace(/\{[a-z_]+\}/g, '');
                sujet = sujet.replace(/\{[a-z_]+\}/g, '');
              } else {
                sujet = `Confirmation de votre rendez-vous - ${vars.nom_entreprise}`;
                contenu = `Bonjour ${vars.client_nom},\n\nVotre rendez-vous pour l'estimation est confirmé!\n\n📅 Date: ${vars.date_rdv}\n🕐 Heure: ${vars.heure_rdv}\n👤 Avec: ${vars.representant}${vars.telephone_representant ? ` (${vars.telephone_representant})` : ''}\n${vars.adresse_travaux ? `📍 Adresse: ${vars.adresse_travaux}\n` : ''}\nPour confirmer votre présence, cliquez sur ce lien:\n(Boutons de confirmation ajoutés automatiquement)\n\nAu plaisir de vous rencontrer!\n\n${vars.nom_entreprise}\n${vars.telephone_entreprise}\n${vars.adresse_entreprise}\n${vars.numero_rbq}`;
              }
              setCreatedRdvId(createdRdvId);
              setEmailData({
                sujet,
                contenu,
                email: formData.client_email,
                _preview: false,
                _mode: 'rdv',
              });
              setShowEmailTemplate(true);
              toast.success(`Dossier ${dossierData.numero_dossier} créé + RDV cédulé. Vérifiez l'aperçu du courriel.`);
              return;
            } catch (prevErr) {
              console.error('Erreur préparation email RDV:', prevErr);
              // Fallback : envoyer direct via l'endpoint dédié
              await axios.post(`${API_URL}/api/crm/rendez-vous/${createdRdvId}/envoyer-confirmation`, {});
              toast.success(`Dossier ${dossierData.numero_dossier} créé + RDV cédulé! Courriel envoyé à ${formData.client_email}.`);
              navigate('/crm/clients');
              return;
            }
          } else {
            toast.success(`Dossier ${dossierData.numero_dossier} créé + RDV cédulé!`);
            navigate('/crm/clients');
            return;
          }
        }
      }
      
      // Si pas de RDV mais "Soumission à faire" coché, mettre à jour le statut
      if (!selectedRdvSlot && soumAFaireSansRdv && dossierData?.dossier_id) {
        try {
          await axios.patch(`${API_URL}/api/crm/dossiers/${dossierData.dossier_id}/marquer-soum-a-faire`,
            dateEcheanceSoum ? { date_echeance: dateEcheanceSoum } : {}
          );
          toast.success(`Dossier ${dossierData.numero_dossier} créé — marqué "Soumission à faire"!`);
          navigate('/crm/clients');
          return;
        } catch (err) {
          console.error('Erreur marquage soum_a_faire:', err);
        }
      }

      toast.success(`Dossier ${dossierData.numero_dossier} créé avec succès!`);
      navigate('/crm/clients');
    } catch (error) {
      console.error('Erreur:', error);
      // Si le dossier a été créé malgré une erreur dans une étape secondaire → naviguer quand même
      if (error?.config?.url?.includes('checklists') || error?.config?.url?.includes('email') || error?.config?.url?.includes('rendez-vous')) {
        toast.success('Dossier créé avec succès (étapes secondaires non complétées)');
        navigate('/crm/clients');
      } else {
        toast.error('Erreur lors de la création : ' + (error.response?.data?.detail || error.message));
        isSubmittingRef.current = false; // Permettre une nouvelle tentative SEULEMENT si la création a échoué
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingRdvEmail(true);
    try {
      // ✅ Mode RDV : utiliser l'endpoint d'envoi manuel après aperçu
      if (emailData._mode === 'rdv' && createdRdvId) {
        await axios.post(`${API_URL}/api/crm/rendez-vous/${createdRdvId}/envoyer-confirmation`, {
          email_sujet: emailData.sujet,
          email_contenu: emailData.contenu,
          custom_email: emailData.email || emailData.destinataire || undefined,
        });
        toast.success('Courriel de confirmation envoyé!');
      } else {
        // Mode soumission classique
        const html = emailData.contenu.replace(/\n/g, '<br>');
        await axios.post(`${API_URL}/api/crm/envoyer-soumission`, {
          dossier_id: createdDossierId,
          email_destinataire: formData.client_email,
          sujet: emailData.sujet,
          contenu: html,
        });
        toast.success('Courriel de confirmation envoyé!');
      }
    } catch (e) {
      toast.error("Erreur lors de l'envoi du courriel");
    } finally {
      setSendingRdvEmail(false);
    }
    setShowEmailTemplate(false);
    navigate('/crm/clients');
  };

  const handleSkipEmail = () => {
    setShowEmailTemplate(false);
    navigate('/crm/clients');
  };

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/crm/dossiers')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux dossiers
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Créer un Nouveau Dossier</h1>
        <p className="text-gray-600 mt-1">Créez un client et son dossier en même temps</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* SECTION 1: Informations Client */}
          <Card>
            <CardHeader>
              <CardTitle>1. Informations du Client</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="client_nom">
                  Nom du Client <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="client_nom"
                  placeholder="Nom complet ou nom de l'entreprise"
                  value={formData.client_nom}
                  onChange={(e) => setFormData({ ...formData, client_nom: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_type">Type de Client</Label>
                  <Select
                    value={formData.client_type}
                    onValueChange={(val) => setFormData({ ...formData, client_type: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residentiel">Résidentiel</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="client_telephone">
                    Téléphone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client_telephone"
                    placeholder="(514) 555-1234"
                    value={formData.client_telephone}
                    onChange={(e) => setFormData({ ...formData, client_telephone: formatPhoneNumber(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="client_email">Email</Label>
                <Input
                  id="client_email"
                  type="email"
                  placeholder="email@example.com"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                />
              </div>

              {/* Contact commercial */}
              {formData.client_type === 'commercial' && (
                <div className="border rounded-lg p-4 bg-purple-50/50 space-y-4">
                  <h4 className="font-semibold text-sm">Contact dans l'entreprise</h4>
                  <div>
                    <Label>Nom du contact</Label>
                    <Input value={formData.contact_nom || ''} onChange={(e) => setFormData({ ...formData, contact_nom: e.target.value })} placeholder="Nom de la personne-ressource" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Téléphone du contact</Label>
                        <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={() => setFormData({ ...formData, contact_telephone: formData.client_telephone })}>
                          <Copy className="w-3 h-3 mr-1" /> Même que l'entreprise
                        </Button>
                      </div>
                      <Input value={formData.contact_telephone || ''} onChange={(e) => setFormData({ ...formData, contact_telephone: formatPhoneNumber(e.target.value) })} placeholder="(514) 555-1234" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Courriel du contact</Label>
                        <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={() => setFormData({ ...formData, contact_email: formData.client_email })}>
                          <Copy className="w-3 h-3 mr-1" /> Même que l'entreprise
                        </Button>
                      </div>
                      <Input type="email" value={formData.contact_email || ''} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} placeholder="contact@entreprise.com" />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="client_adresse">
                  Adresse du Client <span className="text-red-500">*</span>
                </Label>
                {/* 🐛 [Phase 99 Bonus] Autocomplete Google Places — fallback texte libre en cas d'indispo API */}
                <GooglePlacesAutocomplete
                  value={formData.client_adresse}
                  onChange={(val) => setFormData({ ...formData, client_adresse: val })}
                  onPlaceSelected={(details) => setFormData(prev => ({
                    ...prev,
                    client_adresse: details.adresse_complete,
                    client_ville: details.ville || prev.client_ville,
                    client_code_postal: details.code_postal || prev.client_code_postal,
                    // 🐛 [v13.63 Phase 2] Fix B1 : persister coords adresse client
                    //   pour les cas où le dossier n'a PAS d'adresse travaux propre
                    //   (utilise_adresse_client_travaux=true). Sans ça, `Dossier`
                    //   reste sans coords et Validation des heures affiche le
                    //   bandeau « Adresse client non géolocalisée ».
                    client_latitude: details.latitude ?? prev.client_latitude ?? null,
                    client_longitude: details.longitude ?? prev.client_longitude ?? null,
                  }))}
                  placeholder="123 Rue Principale"
                  dataTestId="client-adresse-autocomplete"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_ville">
                    Ville <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client_ville"
                    placeholder="Montréal"
                    value={formData.client_ville}
                    onChange={(e) => setFormData({ ...formData, client_ville: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="client_code_postal">
                    Code Postal <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client_code_postal"
                    placeholder="H1A 1A1"
                    value={formData.client_code_postal}
                    onChange={(e) => setFormData({ ...formData, client_code_postal: e.target.value })}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SECTION 2: Informations Dossier */}
          <Card>
            <CardHeader>
              <CardTitle>2. Informations du Dossier</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="nom_dossier">
                  Nom du Dossier <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nom_dossier"
                  placeholder="Ex: Peinture Façade Extérieure"
                  value={formData.nom_dossier}
                  onChange={(e) => setFormData({ ...formData, nom_dossier: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="type_contrat">Type de Contrat</Label>
                  <Select
                    value={formData.type_contrat}
                    onValueChange={(val) => setFormData({ ...formData, type_contrat: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="peinture">Peinture</SelectItem>
                      <SelectItem value="location">Location</SelectItem>
                      <SelectItem value="a_l_heure">À l'heure</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.type_contrat === 'a_l_heure' && (
                    <p className="text-xs text-blue-600 mt-1">Catégorie stats "À l'heure" assignée automatiquement</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="departement">Département</Label>
                  <Select
                    value={formData.departement}
                    onValueChange={(val) => setFormData({ ...formData, departement: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PRQC">PRQC</SelectItem>
                      <SelectItem value="LMDS">LMDS</SelectItem>
                      <SelectItem value="LOCATION">LOCATION</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="date_creation_dossier">Date de création projet <span className="text-red-500">*</span></Label>
                  <Input
                    id="date_creation_dossier"
                    type="date"
                    value={formData.date_creation}
                    onChange={(e) => setFormData({ ...formData, date_creation: e.target.value })}
                  />
                </div>
              </div>

              {/* La catégorie est définie dans l'onglet Stats du dossier (propriétaire seulement) */}
            </CardContent>
          </Card>

          {/* SECTION NOTES + QUESTIONNAIRE */}
          <Card className="border-amber-200 bg-amber-50/20">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-amber-600" />
                Notes pour la soumission
              </CardTitle>
              <p className="text-sm text-gray-500">
                Pendant l'appel, sélectionnez le type de travaux et répondez aux questions. Tout sera sauvegardé dans les Notes du dossier pour la représentante.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Sélecteur type de travaux */}
              <div>
                <Label className="text-sm font-semibold text-gray-700">Type de travaux</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[
                    { value: 'interieur', label: 'Intérieur', icon: Home, active: 'bg-blue-600 text-white border-blue-600', inactive: 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600' },
                    { value: 'exterieur', label: 'Extérieur', icon: TreePine, active: 'bg-green-600 text-white border-green-600', inactive: 'border-gray-300 text-gray-600 hover:border-green-400 hover:text-green-600' },
                    { value: 'mixte', label: 'Mixte (Int. + Ext.)', icon: Layers, active: 'bg-purple-600 text-white border-purple-600', inactive: 'border-gray-300 text-gray-600 hover:border-purple-400 hover:text-purple-600' },
                  ].map(({ value, label, icon: Icon, active, inactive }) => (
                    <button
                      key={value}
                      type="button"
                      data-testid={`creer-type-${value}`}
                      onClick={() => setFormData(prev => ({ ...prev, type_travaux: prev.type_travaux === value ? '' : value }))}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 font-medium text-sm transition-all bg-white ${formData.type_travaux === value ? active : inactive}`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
                {!formData.type_travaux && (
                  <p className="text-xs text-gray-400 mt-2">Sélectionnez un type pour afficher les questions.</p>
                )}
              </div>

              {/* Questions Intérieur */}
              {(formData.type_travaux === 'interieur' || formData.type_travaux === 'mixte') && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                    <Home className="w-4 h-4" /> Questions — Intérieur
                    <span className="text-xs font-normal text-gray-400 ml-1">
                      ({questionsInt.filter(q => q.reponse).length}/{questionsInt.length} répondues)
                    </span>
                  </p>
                  {questionsInt.map((q, i) => (
                    <QuestionRapide
                      key={q.id}
                      item={q}
                      index={i}
                      onReponse={(val) => setQuestionsInt(prev => prev.map(x => x.id === q.id ? { ...x, reponse: val } : x))}
                    />
                  ))}
                </div>
              )}

              {/* Questions Extérieur */}
              {(formData.type_travaux === 'exterieur' || formData.type_travaux === 'mixte') && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-green-700 flex items-center gap-2">
                    <TreePine className="w-4 h-4" /> Questions — Extérieur
                    <span className="text-xs font-normal text-gray-400 ml-1">
                      ({questionsExt.filter(q => q.reponse).length}/{questionsExt.length} répondues)
                    </span>
                  </p>
                  {questionsExt.map((q, i) => (
                    <QuestionRapide
                      key={q.id}
                      item={q}
                      index={i}
                      onReponse={(val) => setQuestionsExt(prev => prev.map(x => x.id === q.id ? { ...x, reponse: val } : x))}
                    />
                  ))}
                </div>
              )}

              {/* Notes libres */}
              <div>
                <Label className="text-sm font-semibold text-gray-700">
                  Notes libres <span className="text-xs font-normal text-gray-400">(observations, budget, couleurs...)</span>
                </Label>
                <Textarea
                  placeholder="Ex: Client souhaite faire peinturer sa cuisine et son salon. Couleurs à déterminer. Budget approximatif 5000$..."
                  value={formData.notes_projet}
                  onChange={(e) => setFormData({ ...formData, notes_projet: e.target.value })}
                  rows={5}
                  className="bg-white mt-1.5 resize-y min-h-[80px]"
                  style={{ minHeight: '80px' }}
                  data-testid="notes-projet-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* SECTION 3: Adresse des Travaux */}
          <Card>
            <CardHeader>
              <CardTitle>3. Adresse des Travaux</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="utilise_adresse_client"
                  checked={formData.utilise_adresse_client_travaux}
                  onCheckedChange={handleUtiliserAdresseClient}
                />
                <Label htmlFor="utilise_adresse_client" className="cursor-pointer">
                  Utiliser l'adresse du client
                </Label>
              </div>

              <div>
                <Label htmlFor="adresse_travaux">
                  Adresse <span className="text-red-500">*</span>
                </Label>
                {/* 🐛 [Phase 99 Bonus] Autocomplete Google Places pour l'adresse des travaux */}
                {formData.utilise_adresse_client_travaux ? (
                  <Input
                    id="adresse_travaux"
                    placeholder="123 Rue Principale"
                    value={formData.adresse_travaux}
                    disabled
                    required
                  />
                ) : (
                  <GooglePlacesAutocomplete
                    value={formData.adresse_travaux}
                    onChange={(val) => setFormData({ ...formData, adresse_travaux: val })}
                    onPlaceSelected={(details) => setFormData(prev => ({
                      ...prev,
                      adresse_travaux: details.adresse_complete,
                      ville_travaux: details.ville || prev.ville_travaux,
                      code_postal_travaux: details.code_postal || prev.code_postal_travaux,
                      // 🐛 [v13.63 Phase 2 — 2026-02-28] Fix B1 : persister les
                      //   coords retournées par l'autocomplete. Auparavant elles
                      //   étaient JETÉES → dossier créé sans latitude_travaux
                      //   → banner « Adresse client non géolocalisée » dans
                      //   Validation des heures + géocodage lazy backend jamais
                      //   déclenché tant que le 1er punch n'a pas lieu.
                      //   Ref RCA : /app/memory/RCA_V13_63_PHASE2_REPARATION.md
                      latitude_travaux: details.latitude ?? prev.latitude_travaux ?? null,
                      longitude_travaux: details.longitude ?? prev.longitude_travaux ?? null,
                    }))}
                    placeholder="123 Rue Principale"
                    dataTestId="adresse-travaux-autocomplete"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ville_travaux">
                    Ville <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="ville_travaux"
                    placeholder="Montréal"
                    value={formData.ville_travaux}
                    onChange={(e) => setFormData({ ...formData, ville_travaux: e.target.value })}
                    disabled={formData.utilise_adresse_client_travaux}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="code_postal_travaux">
                    Code Postal <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="code_postal_travaux"
                    placeholder="H1A 1A1"
                    value={formData.code_postal_travaux}
                    onChange={(e) => setFormData({ ...formData, code_postal_travaux: e.target.value })}
                    disabled={formData.utilise_adresse_client_travaux}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SECTION 4: Assignation Représentant */}
          <Card>
            <CardHeader>
              <CardTitle>4. Assignation du Représentant</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="representant">Représentant</Label>
                <Select
                  value={formData.representant_id}
                  onValueChange={handleRepresentantChange}
                  disabled={loadingEmployees}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un représentant" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.prenom} {emp.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 mt-2">
                  Le chef d'équipe et le chargé de projet seront assignés plus tard, après la signature du contrat.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* SECTION 5: Rendez-vous Soumission — Calendrier complet */}
          {!rdvScheduled && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  5. Rendez-vous de Soumission (optionnel)
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Sélectionnez une date et un créneau directement dans le calendrier.
                  Les créneaux grisés sont déjà occupés.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Suggestion IA de plage horaire */}
                <SuggestionRdv
                  adresse={formData.adresse_travaux}
                  ville={formData.ville_travaux}
                  representantNom={formData.representant_nom}
                />
                <CalendrierRdvEmbed
                  onSlotSelect={(date, heure, employe_id, employe_nom) => {
                    if (!date) {
                      setSelectedRdvSlot(null);
                    } else {
                      setSelectedRdvSlot({ date, heure, employe_id, employe_nom });
                      setSoumAFaireSansRdv(false); // Désactiver l'option sans RDV si un slot est sélectionné
                    }
                  }}
                  selectedSlot={selectedRdvSlot}
                  defaultEmployeId={formData.representant_id || ''}
                />
                {/* Option "Soumission à faire" sans RDV */}
                {!selectedRdvSlot && (
                  <div className={`border-t pt-4 mt-2 rounded-lg transition-all ${soumAFaireSansRdv ? 'bg-indigo-50/50 border border-indigo-200 p-3' : ''}`}>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="checkbox-soum-a-faire">
                      <input
                        type="checkbox"
                        checked={soumAFaireSansRdv}
                        onChange={e => setSoumAFaireSansRdv(e.target.checked)}
                        className="w-5 h-5 rounded border-indigo-400 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <span className="font-medium text-sm text-indigo-700">Marquer comme "Soumission à faire"</span>
                        <p className="text-xs text-slate-500 mt-0.5">Pas de RDV pour l'instant — le dossier ira dans "Soum. à faire" avec une date d'échéance</p>
                      </div>
                    </label>
                    {soumAFaireSansRdv && (
                      <div className="mt-3 ml-8 flex items-center gap-3">
                        <label className="text-sm text-slate-600 whitespace-nowrap">Date d'échéance :</label>
                        <input
                          type="date"
                          value={dateEcheanceSoum}
                          onChange={e => setDateEcheanceSoum(e.target.value)}
                          className="border border-indigo-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                          min={new Date().toISOString().split('T')[0]}
                          data-testid="input-date-echeance-soum"
                        />
                        <span className="text-xs text-slate-400">(optionnel)</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/crm/dossiers')}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Création...' : 'Créer Client + Dossier'}
            </Button>
          </div>
        </div>
      </form>

      {/* Dialog email confirmation RDV — composant partagé identique au Calendrier RDV */}
      <RdvEmailConfirmationDialog
        open={showEmailTemplate}
        onOpenChange={(o) => { if (!o) handleSkipEmail(); else setShowEmailTemplate(true); }}
        emailData={emailData}
        onChange={setEmailData}
        onSend={handleSendEmail}
        sending={sendingRdvEmail}
        previewHtml={dossierPreviewHtml}
        loadingPreview={dossierLoadingPreview}
        fetchPreview={dossierFetchPreview}
      />
    </div>
  );
}
